import { useRef, useState } from 'react';
import { Button, Group, Stack, TextInput } from '@mantine/core';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import useSaveProductKnowledgeArticle from './hooks/useSaveProductKnowledgeArticle';
import type { ProductKnowledgeArticle } from './types';

type ProductKnowledgeEditorProps = {
  article: ProductKnowledgeArticle | null;
  onClose: () => void;
};

export default function ProductKnowledgeEditor({ article, onClose }: ProductKnowledgeEditorProps) {
  const [title, setTitle] = useState(article?.title ?? '');
  const [description, setDescription] = useState(article?.description ?? '');
  const markdownRef = useRef(article?.markdown ?? '');

  const { mutateAsync: saveArticle, isPending } = useSaveProductKnowledgeArticle();

  const handleSave = async () => {
    if (!title.trim()) return;

    try {
      await saveArticle({
        id: article?.id,
        title: title.trim(),
        description: description.trim(),
        markdown: markdownRef.current,
      });
      onClose();
    } catch (error) {
      notifyAndLogError(
        'Failed to save article',
        'An error occurred while saving the article.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Title"
        placeholder="e.g. Suitability Reports"
        value={title}
        onChange={e => setTitle(e.currentTarget.value)}
        required
      />
      <TextInput
        label="Description"
        placeholder="Brief description shown in the knowledge directory"
        value={description}
        onChange={e => setDescription(e.currentTarget.value)}
      />
      <MarkdownEditor
        defaultValue={markdownRef.current}
        onChange={value => {
          markdownRef.current = value;
        }}
        label="Content"
        rootClass="compact"
      />
      <Group>
        <Button size="xs" onClick={handleSave} loading={isPending} disabled={!title.trim()}>
          Save
        </Button>
        <Button size="xs" variant="subtle" onClick={onClose}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}
